import type { Brand } from '@@fire-b2b/types/Brand'
import { OrderTypeEnum } from '@@fire-b2b/types/order/OrderTypeEnum'
import { fetchBrands } from '@fire-b2b/composables/brands/fetchBrands'
import type { GQLResult } from '@fire-b2b/utils/gql'
import { defineStore } from 'pinia'

export const useBrandsStore = defineStore('brands', () => {
  const isLoaded = ref(false)
  const brandsRef = ref<Brand[]>([])

  const hasBrands = computed(() => {
    return brandsRef.value.length > 0
  })

  const selectedBrandId = ref<number | undefined>(undefined)

  const availableBrandIds = computed(() => {
    return brandsRef.value.map((brand) => brand.shopClientId)
  })

  const defaultBrand = computed(() => {
    return brandsRef.value[0]
  })
  const defaultBrandId = computed(() => {
    return brandsRef.value[0]?.shopClientId
  })

  const selectedBrand = computed(() => {
    return brandsRef.value.find((brand) => {
      return brand.shopClientId === selectedBrandId.value
    })
  })

  const selectedBrandAvatarData = computed(() => {
    return {
      color: selectedBrand.value?.color,
      logo: selectedBrand.value?.brandLogo?.[0]?.path,
    }
  })

  const hasPreOrder = computed(() => {
    return selectedBrand.value?.hasPreOrder ?? false
  })

  const hasReOrder = computed(() => {
    return selectedBrand.value?.hasReOrder ?? false
  })

  const allowedOrderTypes = computed<OrderTypeEnum[]>(() => {
    const types: OrderTypeEnum[] = []
    if (hasPreOrder.value) {
      types.push(OrderTypeEnum.PREORDER)
    }
    if (hasReOrder.value) {
      types.push(OrderTypeEnum.REORDER)
    }
    return types
  })

  const defaultOrderType = computed<OrderTypeEnum>(() => {
    if (hasPreOrder.value) {
      return OrderTypeEnum.PREORDER
    } else if (hasReOrder.value) {
      return OrderTypeEnum.REORDER
    }
    return OrderTypeEnum.PREORDER
  })

  async function loadBrands(): Promise<GQLResult<void>> {
    if (isLoaded.value) {
      return ok()
    }

    const brandsResult = await fetchBrands()

    if (isErr(brandsResult)) {
      return brandsResult
    }

    brandsRef.value = brandsResult.data
    isLoaded.value = true

    const firstBrand = brandsRef.value[0]
    if (firstBrand && !selectedBrandId.value) {
      selectedBrandId.value = firstBrand.shopClientId
    }

    return ok()
  }

  function brandIdExists(id: number): id is number {
    return brandsRef.value.some((b) => b.shopClientId === id)
  }

  function getBrand(id: number) {
    return brandsRef.value.find((b) => b.shopClientId === id)
  }

  return {
    isLoaded,
    brands: brandsRef,
    hasBrands,
    defaultBrand,
    defaultBrandId,
    selectedBrand,
    selectedBrandId,
    selectedBrandAvatarData,
    hasPreOrder,
    hasReOrder,
    allowedOrderTypes,
    defaultOrderType,
    availableBrandIds,
    loadBrands,
    brandIdExists,
    getBrand,
    setSelectedBrand: (id: number) => {
      selectedBrandId.value = id
    },
  }
})
